import { useState } from 'react';
import { ChevronDown, ChevronUp, Phone, Volume2 } from 'lucide-react';
import type { TaskTimelineEvent } from '@vagusx/clients';
import { BookingPill } from '@/components/interactions/BookingPill';
import { CallStatusPill } from '@/components/interactions/CallStatusPill';
import {
  CancellationFeePill,
  cancellationFeePillFor,
} from '@/components/interactions/CancellationFeePill';
import {
  BookingClaimMismatchBadge,
  EvaluationClientSummary,
  EvaluationDetail,
  hasBookingClaimMismatch,
} from '@/components/interactions/CallEvaluationSection';
import { useInteraction, useInteractionEvaluation } from '@/hooks/use-interactions';
import { useTaskTimeline } from './use-task-overview';

/**
 * How the last call on this task went — read from colour and icons first,
 * prose only if you want it.
 *
 * Sits directly under "Your next step" (the Call-to-schedule button) because
 * that is the reading order that matters: what to do next, then how the last
 * attempt went. The call history is already on this tab via `ActivityTimeline`,
 * but that is collapsed by default and merges calls with task history and
 * notes, so the one fact you want before dialling again is buried.
 *
 * ── The signal row comes first ───────────────────────────────────────────
 * Status tag then booking flag, above the headline, because those two answer
 * "does anyone need to do something" and "did we get an appointment" without
 * a word being read. A third joins them only when it fires: the booking-claim
 * alarm, which contradicts the booking flag beside it and is the one verdict
 * on this card that must not sit behind the expand.
 *
 * The card used to lead with a patient-initials avatar and push the status tag
 * to the right edge of the headline row — the most prominent element carried
 * the least information, on a page where the operator already knows whose task
 * they are looking at.
 *
 * ── Why this expands in place rather than opening the sheet ──────────────
 * The operator is on this page deciding whether to dial again; the last call's
 * verdict is CONTEXT for that decision, not a separate destination. Covering
 * the page with `InteractionDetailSheet` to read four bullet points loses the
 * next-step panel they were looking at. The sheet is still one click away for
 * the recording and transcript, which genuinely are a destination.
 *
 * Collapsed shows the WHOLE client block (headline, office note, callback
 * badge) — everything before the category rows. It used to `truncate` the
 * headline to one line, which clipped it mid-sentence and dropped the office
 * note entirely: the one line that says what the practice owes on this call.
 *
 * `useTaskTimeline` is already called elsewhere on this tab with the same query
 * key, so it resolves from cache. Only the evaluation is a fresh request.
 */

interface LastCallSectionProps {
  taskId: string;
  /**
   * Open the Call detail sheet for this interaction.
   *
   * This section no longer OWNS a sheet. `ActivityTimeline` renders one too,
   * and both mount on this tab — once the open call lives in the URL, a
   * locally-owned sheet in each would mean two stacked sheets for the same
   * `?call=` id. `TaskOverviewTab` renders the single sheet; this reports the
   * click.
   */
  onOpenCall: (interactionId: string) => void;
}

/**
 * The most recent VOICE call on this task, or null.
 *
 * The timeline is already sorted newest-first server-side
 * (`TaskOverviewService.getTimeline`), so the first match wins. Filtering to
 * `voice` matters: the same event stream carries SMS interactions, and an SMS
 * sent after the call would otherwise render as "the last call".
 *
 * Exported for its unit test — it is the only real logic in this file.
 */
export function findMostRecentCall(
  events: TaskTimelineEvent[] | undefined
): TaskTimelineEvent | null {
  if (!events) return null;
  return (
    events.find((event) => event.kind === 'interaction' && event.payload.channel === 'voice') ??
    null
  );
}

export function LastCallSection({
  taskId,
  onOpenCall,
}: LastCallSectionProps): React.ReactElement | null {
  const [expanded, setExpanded] = useState(false);
  const { data: timeline } = useTaskTimeline(taskId);

  const lastCall = findMostRecentCall(timeline?.events);
  // Safe to call unconditionally — the hook is disabled on an undefined id.
  const { data: evaluation } = useInteractionEvaluation(lastCall?.id);
  // The interaction read carries the late-cancellation fee accepted on the call
  // (ADR-290), from the appointment record — so it shows whether or not the
  // evaluator has run. Same query key as the call sheet, so opening the call
  // reuses it.
  const { data: interaction } = useInteraction(lastCall?.id);
  const fee = interaction?.cancellationFeeCents;

  // No call on this task — render nothing rather than an empty-state row. Most
  // referrals have never been dialled (that is what the Call button is for), so
  // a "no calls yet" row would add noise to every un-actioned referral.
  // `NextStepSection` and `TaskListSection` self-hide on this tab for the same
  // reason.
  if (!lastCall || lastCall.kind !== 'interaction') return null;

  const timestamp = (
    <span className="text-xs text-muted-foreground">
      · {new Date(lastCall.timestamp).toLocaleString()}
    </span>
  );

  // An evaluation lands seconds-to-minutes after the call, so "call happened,
  // not yet judged" is a real and briefly-visible state. It renders with no
  // flag at all: an unevaluated call is NOT a clean call, and a green tag here
  // would be a claim we haven't earned. Inbound calls stay in this state
  // permanently — the evaluator is outbound-only.
  //
  // There is nothing to expand INTO in that state, so the row keeps its old
  // behaviour and opens the sheet directly. An expand control that reveals an
  // empty block is worse than the panel it replaced.
  if (!evaluation) {
    const line = lastCall.payload.summary?.trim() || 'Call completed.';
    return (
      <section className="rounded-lg border bg-card p-4">
        <div className="mb-2 flex items-center gap-2">
          <Phone className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Last call</h3>
          {timestamp}
        </div>

        {/* Unevaluated still shows the fee: it comes from the appointment
            record, not the verdict this branch is waiting for. */}
        {cancellationFeePillFor(fee) && (
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <CancellationFeePill cancellationFeeCents={fee} />
          </div>
        )}

        <button
          type="button"
          onClick={() => onOpenCall(lastCall.id)}
          className="flex w-full items-center gap-3 rounded-md p-1 text-left transition-colors hover:bg-muted/50"
        >
          <p className="min-w-0 flex-1 text-sm text-foreground">{line}</p>
        </button>
      </section>
    );
  }

  return (
    <section className="rounded-lg border bg-card p-4">
      {/* The whole client block is the toggle. Tier 1 and the signal row have
          no interactive descendants — only text, icons and badges — so they are
          valid inside a button, and it means a click anywhere on the summary
          expands rather than the operator having to find a chevron. */}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="w-full space-y-3 rounded-md p-1 text-left transition-colors hover:bg-muted/50"
      >
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Last call</h3>
          {timestamp}
        </div>

        {/* The flags that answer the whole card at a glance. `showStatusPill`
            is off below so the same pill doesn't render twice, and
            `showBookingClaimAlarm` is off for the same reason on the badge. */}
        <div className="flex flex-wrap items-center gap-2">
          {evaluation.status && <CallStatusPill status={evaluation.status} />}
          <CancellationFeePill cancellationFeeCents={fee} />
          <BookingPill
            outcomeCategory={evaluation.outcomeCategory}
            bookedOnThisCall={evaluation.bookedOnThisCall}
          />
          {/* The one thing on this card that must not wait for an expand. This
              is the page where the operator decides whether to dial again, and a
              claimed-but-unrecorded booking changes that decision completely:
              the patient believes they have an appointment nobody has. It lands
              beside the `BookingPill`, so "AI claimed booked" sits against "No
              appointment booked" and the contradiction needs no explaining.
              Renders for nobody else — the verdict it reads is admin-only and
              absent from every other viewer's payload. */}
          {hasBookingClaimMismatch(evaluation) && <BookingClaimMismatchBadge compact />}
        </div>

        <EvaluationClientSummary evaluation={evaluation} showStatusPill={false} />
      </button>

      {expanded && (
        <div className="mt-3 space-y-3 px-1">
          <EvaluationDetail evaluation={evaluation} showBookingClaimAlarm={false} />

          {/* The recording and the transcript ARE a destination — reviewing
              them is its own task, not context for the next dial — so they
              stay behind the sheet. */}
          <button
            type="button"
            onClick={() => onOpenCall(lastCall.id)}
            className="flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
          >
            <Volume2 className="h-3.5 w-3.5" />
            View recording &amp; transcript
          </button>
        </div>
      )}

      {/* The expand affordance lives at the FOOT of the card, not as a chevron
          crowding the "Last call" title. A labelled row says what is behind it,
          which a bare arrow next to a heading never did, and it sits where the
          eye already is after reading the summary. The summary block above is
          still the primary click target — this row is the hint. */}
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="mt-2 flex w-full items-center justify-center gap-1 border-t pt-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {expanded ? 'Hide call details' : 'Show call details'}
      </button>
    </section>
  );
}
